<template>
  <div class="profile-page">
    <!-- 未登录状态 -->
    <div v-if="!isLoggedIn" class="login-section">
      <div class="login-card">
        <h2>登录 / 注册</h2>
        
        <!-- 登录方式切换 -->
        <div class="login-tabs">
          <button :class="{ active: loginType === 'password' }" @click="loginType = 'password'">密码登录</button>
       
          <button :class="{ active: loginType === 'register' }" @click="loginType = 'register'">注册</button>
        </div>

        <div class="login-form">
          <!-- 密码登录 -->
          <template v-if="loginType === 'password'">
            <div class="form-group">
              <label>用户名/手机号</label>
              <input 
                v-model="loginForm.username" 
                type="text" 
                placeholder="请输入用户名或手机号"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>密码</label>
              <input 
                v-model="loginForm.password" 
                type="password" 
                placeholder="请输入密码"
                class="form-input"
              />
            </div>
            <button @click="login" class="login-btn">登录</button>
          </template>

          <!-- 短信登录 -->
       

          <!-- 注册 -->
          <template v-else-if="loginType === 'register'">
            <div class="form-group">
              <label>手机号</label>
              <input 
                v-model="registerForm.mobile" 
                type="tel" 
                placeholder="请输入手机号"
                class="form-input"
                maxlength="11"
              />
            </div>
            <div class="form-group">
              <label>验证码</label>
              <div class="sms-code-group">
                <input 
                  v-model="registerForm.captcha" 
                  type="text" 
                  placeholder="请输入验证码"
                  class="form-input"
                  maxlength="6"
                />
                <button 
                  @click="sendRegisterSms" 
                  class="sms-code-btn"
                  :disabled="smsCountdown > 0"
                >
                  {{ smsCountdown > 0 ? smsCountdown + 's' : '获取验证码' }}
                </button>
              </div>
            </div>
            <div class="form-group">
              <label>设置密码</label>
              <input 
                v-model="registerForm.password" 
                type="password" 
                placeholder="请设置密码（6位以上）"
                class="form-input"
              />
            </div>
            <button @click="smsRegister" class="login-btn">注册并登录</button>
          </template>
        </div>
      </div>
    </div>

    <!-- 已登录状态 -->
    <div v-else class="profile-content">
      <!-- 用户信息卡片 -->
      <div class="user-card">
        <div class="user-avatar">
          <img :src="userInfo.avatar || defaultAvatar" alt="头像" />
        </div>
        <div class="user-info">
          <h3>{{ userInfo.username }}</h3>
          <p>等级：{{ userInfo.level }}</p>
          <p>注册时间：{{ userInfo.registerDate }}</p>
          <!-- VIP信息展示 -->
          <div class="vip-info" v-if="vipInfo.isVip">
            <span class="vip-badge">VIP</span>
            <p class="vip-details">{{ vipInfo.packageName }}</p>
            <p class="vip-expiry">到期时间：{{ vipInfo.endTime }}</p>
            <p class="vip-days">剩余天数：{{ vipInfo.remainingDays }}天</p>
          </div>
          <div class="non-vip" v-else>
            <p>普通用户</p>
          </div>
        </div>
        <button @click="logout" class="logout-btn">退出登录</button>
      </div>

      <!-- 功能列表 -->
      <div class="feature-list">
        <!-- 阅读历史 -->
        <div class="feature-item" @click="showReadingHistory = true">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">阅读历史</div>
            <div class="feature-sub">本地浏览记录</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>

        <!-- 青少年模式 -->
        <div class="feature-item" @click="navigateToYouthMode">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">青少年模式</div>
            <div class="feature-sub">{{ youthModeEnabled ? '已开启' : '未开启' }}</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>

        <!-- 我的投稿 -->
        <div class="feature-item" @click="showMySubmissionsModal">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">我的投稿</div>
            <div class="feature-sub">管理投稿作品</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>

        <!-- 作者申请 -->
        <div class="feature-item" @click="goToAuthorApplication">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/><polyline points="12 11 12 13"/><line x1="12" y1="13" x2="12" y2="16"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">作者申请</div>
            <div class="feature-sub">{{ getAuthorStatusText(userInfo.author_status) }}</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>

        <!-- 阅读进度 -->
        <div class="feature-item" @click="$router.push('/reading-history')">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14.5"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">阅读进度</div>
            <div class="feature-sub">云端同步记录</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>

        <!-- 我的收藏 -->
        <div class="feature-item" @click="$router.push('/favorites')">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">我的收藏</div>
            <div class="feature-sub">追更的漫画</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>

        <!-- 我的评论 -->
        <div class="feature-item" @click="$router.push('/my-comments')">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">我的评论</div>
            <div class="feature-sub">发表过的书评</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>

        <!-- 购买历史 -->
        <div class="feature-item" @click="$router.push('/purchase-history')">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1.2"/><circle cx="20" cy="21" r="1.2"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">购买历史</div>
            <div class="feature-sub">章节与会员订单</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>

        <!-- 修改资料 -->
        <div class="feature-item" @click="openEditProfile">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">修改资料</div>
            <div class="feature-sub">头像 / 昵称 / 密码</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>

        <!-- 设置 -->
        <div class="feature-item" @click="showSettings = true">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">设置</div>
            <div class="feature-sub">主题 / 通知 / 缓存</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>

        <!-- 实名认证 -->
        <div class="feature-item" @click="showRealnameModal = true">
          <div class="feature-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
          </div>
          <div class="feature-text">
            <div class="feature-title">实名认证</div>
            <div class="feature-sub">{{ userInfo.realname_verified ? '已认证' : '未认证' }}</div>
          </div>
          <svg class="feature-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </div>
      </div>

      <!-- 阅读历史弹窗 -->
      <div v-if="showReadingHistory" class="modal-overlay" @click="showReadingHistory = false">
        <div class="modal-content reading-history-modal" @click.stop>
          <div class="modal-header">
            <h3>阅读历史</h3>
            <div class="header-actions">
              <button @click="clearAllHistory" class="clear-all-btn" v-if="readingHistory.length > 0">清空</button>
              <button @click="showReadingHistory = false" class="close-btn">×</button>
            </div>
          </div>

          <!-- 阅读统计 -->
          <div class="reading-stats" v-if="readingHistory.length > 0">
            <div class="stat-item">
              <span class="stat-number">{{ readingStats.totalComics }}</span>
              <span class="stat-label">漫画</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">{{ readingStats.totalChapters }}</span>
              <span class="stat-label">章节</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">{{ readingStats.completedChapters }}</span>
              <span class="stat-label">已完成</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">{{ Math.round(readingStats.totalReadingTime / 60) }}</span>
              <span class="stat-label">小时</span>
            </div>
          </div>

          <div class="modal-body">
            <div v-if="readingHistory.length === 0" class="empty-state">
              <div class="empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5v14z"/><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/></svg>
              </div>
              <p>暂无阅读记录</p>
              <p class="empty-tip">开始阅读漫画后，这里会显示您的阅读历史</p>
            </div>
            <div v-else class="history-list">
              <div
                v-for="item in readingHistory"
                :key="item.comicId"
                class="history-item"
                @click="continueReading(item)"
              >
                <img :src="getCoverImageUrl(item.coverImage)"
                     :alt="item.title"
                     class="history-cover" />
                <div class="history-info">
                  <h4>{{ item.title }}</h4>
                  <p class="chapter-title">{{ item.chapterTitle || '第' + item.currentPage + '页' }}</p>
                  <div class="progress-info">
                    <span class="progress-text">{{ item.currentPage }}/{{ item.totalPages || '?' }}页</span>
                    <div class="progress-bar">
                      <div class="progress-fill" :style="{ width: ((item.currentPage / (item.totalPages || 1)) * 100) + '%' }"></div>
                    </div>
                    <span class="progress-percent">{{ Math.round((item.currentPage / (item.totalPages || 1)) * 100) }}%</span>
                  </div>
                  <p class="history-time">{{ formatTime(item.lastReadTime) }}</p>
                </div>
                <div class="history-actions">
                  <span v-if="item.progress >= 100" class="completed-badge">已完成</span>
                  <button @click.stop="removeHistory(item.comicId)" class="remove-btn">删除</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 我的投稿弹窗 -->
      <div v-if="showMySubmissions" class="modal-overlay" @click="showMySubmissions = false">
        <div class="modal-content submissions-modal" @click.stop>
          <div class="modal-header">
            <h3>我的投稿</h3>
            <div class="header-actions">
              <select v-model="submissionFilter" @change="loadSubmissions" class="filter-select">
                <option value="">全部状态</option>
                <option value="pending">待审核</option>
                <option value="approved">已通过</option>
                <option value="rejected">已拒绝</option>
              </select>
              <button @click="showMySubmissions = false" class="close-btn">×</button>
            </div>
          </div>

          <div class="modal-body">
            <div v-if="loadingSubmissions" class="loading-state">
              <div class="loading-spinner"></div>
              <p>加载中...</p>
            </div>

            <div v-else-if="submissions.length === 0" class="empty-state">
              <div class="empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
              </div>
              <p>暂无投稿作品</p>
              <p class="empty-tip">
                <router-link to="/submission" @click="showMySubmissions = false">立即投稿</router-link>
                分享您的创作
              </p>
            </div>

            <div v-else class="submissions-list">
              <div
                v-for="submission in submissions"
                :key="submission.id"
                class="submission-item"
              >
                <img :src="getSubmissionCover(submission)" :alt="submission.title" class="submission-cover" />
                <div class="submission-info">
                  <h4>{{ submission.title }}</h4>
                  <p class="submission-author">作者：{{ submission.author }}</p>
                  <p class="submission-desc">{{ submission.description }}</p>
                  <div class="submission-meta">
                    <span class="submission-status" :class="getStatusClass(submission.audit_status)">
                      {{ getStatusText(submission.audit_status) }}
                    </span>
                    <span class="submission-date">{{ formatDate(submission.created_at) }}</span>
                    <span class="submission-chapters">{{ submission.chapter_count || 0 }}章</span>
                  </div>
                </div>
                <div class="submission-actions">
                  <button @click="editSubmission(submission)" class="action-btn edit-btn">编辑</button>
                  <button @click="deleteSubmissionConfirm(submission)" class="action-btn delete-btn">删除</button>
                </div>
              </div>
            </div>

            <!-- 分页 -->
            <div v-if="totalPages > 1" class="pagination">
              <button
                @click="changePage(currentPage - 1)"
                :disabled="currentPage <= 1"
                class="page-btn"
              >
                上一页
              </button>
              <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
              <button
                @click="changePage(currentPage + 1)"
                :disabled="currentPage >= totalPages"
                class="page-btn"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 投稿编辑弹窗 -->
      <div v-if="showEditSubmission" class="modal-overlay" @click="showEditSubmission = false">
        <div class="modal-content edit-submission-modal" @click.stop>
          <div class="modal-header">
            <h3>编辑投稿</h3>
            <button @click="showEditSubmission = false" class="close-btn">×</button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveSubmissionEdit" class="edit-form">
              <div class="form-group">
                <label for="edit-title">作品标题 *</label>
                <input
                  type="text"
                  id="edit-title"
                  v-model="editingSubmission.title"
                  required
                  maxlength="100"
                />
              </div>

              <div class="form-group">
                <label for="edit-description">作品描述 *</label>
                <textarea
                  id="edit-description"
                  v-model="editingSubmission.description"
                  required
                  maxlength="500"
                  rows="4"
                ></textarea>
              </div>

              <div class="form-group">
                <label for="edit-tags">标签</label>
                <input
                  type="text"
                  id="edit-tags"
                  v-model="editingSubmission.tags"
                  placeholder="用逗号分隔多个标签"
                  maxlength="100"
                />
              </div>

              <div class="form-actions">
                <button type="button" @click="showEditSubmission = false" class="cancel-btn">取消</button>
                <button type="submit" :disabled="savingEdit" class="save-btn">
                  {{ savingEdit ? '保存中...' : '保存' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- 修改资料弹窗 -->
      <div v-if="showEditProfile" class="modal-overlay" @click="showEditProfile = false">
        <div class="modal-content edit-profile-modal" @click.stop>
          <div class="modal-header">
            <h3>修改资料</h3>
            <button @click="showEditProfile = false" class="close-btn">×</button>
          </div>
          <div class="modal-body">
            <div class="profile-form">
              <!-- 头像上传 -->
              <div class="form-group avatar-group">
                <label>头像</label>
                <div class="avatar-upload">
                  <div class="current-avatar">
                    <img :src="profileForm.avatar || userInfo.avatar || defaultAvatar" alt="当前头像" />
                  </div>
                  <div class="upload-controls">
                    <input type="file" ref="avatarInput" @change="handleAvatarUpload" accept="image/*" style="display: none;" />
                    <button @click="$refs.avatarInput.click()" class="upload-btn" :disabled="uploadingAvatar">
                      {{ uploadingAvatar ? '上传中...' : '选择头像' }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- 用户名 -->
              <div class="form-group">
                <label>用户名</label>
                <input 
                  v-model="profileForm.username" 
                  type="text" 
                  placeholder="请输入用户名"
                  class="form-input"
                  maxlength="20"
                />
              </div>

              <!-- 昵称 -->
              <div class="form-group">
                <label>昵称</label>
                <input 
                  v-model="profileForm.nickname" 
                  type="text" 
                  placeholder="请输入昵称"
                  class="form-input"
                  maxlength="20"
                />
              </div>

              <!-- 个人简介 -->
              <div class="form-group">
                <label>个人简介</label>
                <textarea 
                  v-model="profileForm.bio" 
                  placeholder="请输入个人简介"
                  class="form-textarea"
                  maxlength="200"
                  rows="4"
                ></textarea>
                <div class="char-count">{{ profileForm.bio.length }}/200</div>
              </div>
            </div>

            <div class="modal-actions">
              <button @click="showEditProfile = false" class="cancel-btn">取消</button>
              <button @click="saveProfile" class="save-btn" :disabled="savingProfile">
                {{ savingProfile ? '保存中...' : '保存' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 设置弹窗 -->
      <div v-if="showSettings" class="modal-overlay" @click="showSettings = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>设置</h3>
            <button @click="showSettings = false" class="close-btn">×</button>
          </div>
          <div class="modal-body">
            <div class="setting-item">
              <label>自动保存阅读进度</label>
              <input type="checkbox" v-model="settings.autoSave" />
            </div>
            <div class="setting-item">
              <label>夜间模式</label>
              <input type="checkbox" v-model="settings.nightMode" />
            </div>
            <div class="setting-item">
              <label>字体大小</label>
              <select v-model="settings.fontSize">
                <option value="small">小</option>
                <option value="medium">中</option>
                <option value="large">大</option>
              </select>
            </div>
            <button @click="saveSettings" class="save-btn">保存设置</button>
          </div>
        </div>
      </div>

      <!-- 实名认证弹窗 -->
      <div v-if="showRealnameModal" class="modal-overlay" @click="showRealnameModal = false">
        <div class="modal-content realname-modal" @click.stop>
          <div class="modal-header">
            <h3>实名认证</h3>
            <button @click="showRealnameModal = false" class="close-btn">×</button>
          </div>
          <div class="modal-body">
            <div v-if="userInfo.realname_verified" class="verified-info">
              <div class="verified-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <p class="verified-text">已认证</p>
              <p class="verified-detail">姓名：{{ userInfo.realname }}</p>
              <p class="verified-detail">身份证：**** **** **** {{ userInfo.idcard_last }}</p>
            </div>
            <form v-else @submit.prevent="submitRealname" class="realname-form">
              <div class="form-group">
                <label>真实姓名</label>
                <input 
                  v-model="realnameForm.realname" 
                  type="text" 
                  placeholder="请输入真实姓名"
                  class="form-input"
                  maxlength="20"
                />
              </div>
              <div class="form-group">
                <label>身份证正面照（必填）</label>
                <div class="idcard-upload-area" @click="triggerUpload('front')">
                  <input 
                    type="file" 
                    ref="idcardFrontInput" 
                    accept="image/*" 
                    @change="handleIdcardFrontUpload" 
                    style="display: none;"
                  />
                  <div v-if="realnameForm.idcard_front" class="upload-preview">
                    <img :src="realnameForm.idcard_front" alt="身份证正面" />
                    <span class="preview-tip">点击更换</span>
                  </div>
                  <div v-else class="upload-placeholder">
                    <span class="upload-icon">+</span>
                    <span>点击上传身份证正面照</span>
                  </div>
                </div>
                <p class="upload-tip">请确保照片清晰可辨，人像和文字都清晰可见</p>
              </div>
              <div class="form-group">
                <label>身份证反面照（选填）</label>
                <div class="idcard-upload-area" @click="triggerUpload('back')">
                  <input 
                    type="file" 
                    ref="idcardBackInput" 
                    accept="image/*" 
                    @change="handleIdcardBackUpload" 
                    style="display: none;"
                  />
                  <div v-if="realnameForm.idcard_back" class="upload-preview">
                    <img :src="realnameForm.idcard_back" alt="身份证反面" />
                    <span class="preview-tip">点击更换</span>
                  </div>
                  <div v-else class="upload-placeholder">
                    <span class="upload-icon">+</span>
                    <span>点击上传身份证反面照</span>
                  </div>
                </div>
              </div>
              <p class="form-tip">请确保信息真实有效，实名认证后可享受更多服务</p>
              <button type="submit" class="save-btn" :disabled="submittingRealname || !realnameForm.idcard_front">
                {{ submittingRealname ? '提交中...' : '提交认证' }}
              </button>
            </form>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {
  userLogin,
  userRegister,
  getUserInfo,
  getUserVipInfo,
  ReadingProgressManager,
  BASE_URL,
  getUserSubmissions,
  getSubmissionDetail,
  editSubmission,
  deleteSubmission,
  updateUserProfile,
  uploadFile,
  realnameVerify,
  sendSmsCode,
  checkSmsCode,
  smsLogin,
  getYouthConfig,
  getFavorites,
  toggleFavorite,
  getMyComments,
  deleteCommentApi,
  getPurchasedChapters,
  getReadingHistory,
  deleteReadingProgress,
  getReadingProgress as getReadingProgressApi,
} from '../api/index';

export default {
  name: 'ProfilePage',
  data() {
    return {
      isLoggedIn: false,
      loginType: 'password', // password, sms, register
      loginForm: {
        username: '',
        password: ''
      },
      smsLoginForm: {
        mobile: '',
        captcha: ''
      },
      registerForm: {
        mobile: '',
        captcha: '',
        password: ''
      },
      smsCountdown: 0,
      smsTimer: null,
      userInfo: {
        username: '用户',
        level: 'LV.1',
        registerDate: '2023-01-01',
        avatar: '',
        realname_verified: 0,
        realname: '',
        idcard_last: '',
        birthday: '',
        is_author: false,
        author_status: 'none'
      },
      defaultAvatar: '/logo.png',
      showReadingHistory: false,
      showMySubmissions: false,
      showEditSubmission: false,
      showSettings: false,
      showRealnameModal: false,
      readingHistory: [],
      readingStats: {
        totalComics: 0,
        totalChapters: 0,
        completedChapters: 0,
        totalReadingTime: 0,
        averageProgress: 0
      },
      settings: {
        autoSave: true,
        nightMode: false,
        fontSize: 'medium'
      },

      // 投稿管理相关
      submissions: [],
      loadingSubmissions: false,
      submissionFilter: '',
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,

      // 编辑投稿相关
      editingSubmission: {
        id: null,
        title: '',
        description: '',
        tags: ''
      },
      savingEdit: false,
      vipInfo: {
        isVip: false,
        packageName: '',
        endTime: '',
        remainingDays: 0
      },

      // 修改资料相关
      showEditProfile: false,
      profileForm: {
        avatar: '',
        username: '',
        nickname: '',
        bio: ''
      },
      uploadingAvatar: false,
      savingProfile: false,

      // 实名认证相关
      realnameForm: {
        realname: '',
        idcard_front: '',
        idcard_back: '',
        idcard_front_file: null,
        idcard_back_file: null
      },
      submittingRealname: false,

      // 青少年模式真实开启状态（从 youth_mode 表读取）
      youthModeEnabled: false
    }
  },
  mounted() {
    this.checkLoginStatus();
    this.loadUserData();
  },
  beforeUnmount() {
    if (this.smsTimer) {
      clearInterval(this.smsTimer);
    }
  },
  methods: {
    // 检查登录状态
    // ===== 内联页面方法（已停用，保留备用） =====
    // 阅读进度（后端阅读历史）
    async loadReadingHistoryData() {
      this.readingHistoryLoading = true;
      try {
        const res = await getReadingHistory({ page: 1, limit: 50 });
        if (res.data && res.data.code === 1) {
          this.readingHistoryData = res.data.data.list || [];
        }
      } catch (e) {
        console.error('加载阅读历史失败:', e);
      } finally {
        this.readingHistoryLoading = false;
      }
    },
    continueReadingProgress(item) {
      this.$router.push(`/reader/${item.chapter_id}/${item.comic_id}`);
    },
    async deleteReadingItem(comicId) {
      if (!confirm('确定要删除这条阅读记录吗？')) return;
      try {
        const res = await deleteReadingProgress(comicId);
        if (res.data && res.data.code === 1) {
          this.readingHistoryData = this.readingHistoryData.filter(i => i.comic_id !== comicId);
        }
      } catch (e) {
        console.error('删除阅读记录失败:', e);
        alert('删除失败');
      }
    },

    // 我的收藏
    async loadFavorites() {
      this.favoritesLoading = true;
      try {
        const res = await getFavorites({ page: 1, limit: 50 });
        if (res.data && res.data.code === 1) {
          this.favorites = res.data.data.list || [];
        }
      } catch (e) {
        console.error('加载收藏失败:', e);
      } finally {
        this.favoritesLoading = false;
      }
    },
    goToComic(comicId) {
      if (!comicId) return;
      this.$router.push(`/comic/${comicId}`);
    },
    async removeFavorite(comicId) {
      if (!confirm('确定要取消收藏吗？')) return;
      try {
        const res = await toggleFavorite(comicId);
        if (res.data && res.data.code === 1) {
          this.favorites = this.favorites.filter(i => i.comic_id !== comicId);
        }
      } catch (e) {
        console.error('取消收藏失败:', e);
        alert('操作失败');
      }
    },

    // 我的评论
    async loadComments() {
      this.commentsLoading = true;
      try {
        const res = await getMyComments({ page: 1, limit: 50 });
        if (res.data && res.data.code === 1) {
          this.comments = res.data.data.list || [];
        }
      } catch (e) {
        console.error('加载我的评论失败:', e);
      } finally {
        this.commentsLoading = false;
      }
    },
    async deleteComment(commentId) {
      if (!confirm('确定要删除这条评论吗？')) return;
      try {
        const res = await deleteCommentApi(commentId);
        if (res.data && res.data.code === 1) {
          this.comments = this.comments.filter(c => c.id !== commentId);
        }
      } catch (e) {
        console.error('删除评论失败:', e);
        alert('删除失败');
      }
    },

    // 购买历史（章节购买历史）
    async loadPurchases() {
      this.purchasesLoading = true;
      this.purchasePage = 1;
      try {
        const res = await getPurchasedChapters({ page: this.purchasePage, limit: this.purchaseLimit });
        if (res.data && res.data.code === 1) {
          this.purchases = res.data.data.list || [];
          this.purchaseTotal = res.data.data.total || 0;
        }
      } catch (e) {
        console.error('加载购买历史失败:', e);
      } finally {
        this.purchasesLoading = false;
      }
    },
    async loadMorePurchases() {
      if (this.purchaseLoadingMore) return;
      this.purchaseLoadingMore = true;
      this.purchasePage++;
      try {
        const res = await getPurchasedChapters({ page: this.purchasePage, limit: this.purchaseLimit });
        if (res.data && res.data.code === 1) {
          const list = res.data.data.list || [];
          this.purchases = [...this.purchases, ...list];
        }
      } catch (e) {
        console.error('加载更多购买历史失败:', e);
      } finally {
        this.purchaseLoadingMore = false;
      }
    },
    formatTabTime(timestamp) {
      if (!timestamp) return '';
      const ts = String(timestamp).length === 10 ? timestamp * 1000 : Number(timestamp);
      const d = new Date(ts);
      if (isNaN(d.getTime())) return '';
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    },
    goDetailFromItem(item) {
      const id = item.comic_id || item.id;
      if (id) this.$router.push({ name: 'ComicDetail', params: { id } });
    },

    checkLoginStatus() {
      const token = localStorage.getItem('userToken');
      this.isLoggedIn = !!token;
      if (token) {
        this.fetchUserInfo(token);
        this.fetchVipInfo();
        this.fetchYouthModeStatus();
      }
    },

    // 获取青少年模式真实开启状态（从 youth_mode 表读取）
    async fetchYouthModeStatus() {
      try {
        const res = await getYouthConfig();
        if (res.data && res.data.code === 1) {
          this.youthModeEnabled = !!res.data.data.enabled;
        }
      } catch (e) {
        console.warn('获取青少年模式状态失败', e);
      }
    },

    // 登录
    async login() {
      if (!this.loginForm.username || !this.loginForm.password) {
        alert('请输入用户名和密码');
        return;
      }
      try {
        const res = await userLogin({ account: this.loginForm.username, password: this.loginForm.password });
        if (res.data && res.data.code === 1) {
          const token = res.data.data.userinfo.token;
          const username = res.data.data.userinfo.username || this.loginForm.username;
          
          localStorage.setItem('userToken', token);
          localStorage.setItem('username', username);
          
          this.isLoggedIn = true;
          this.fetchUserInfo(token);
          this.fetchVipInfo();
          this.loginForm.username = '';
          this.loginForm.password = '';
          this.loginType = 'password';
          alert('登录成功！');
          window.dispatchEvent(new Event('app:auth-changed'));
          if (this.$route.query.redirect) {
            this.$router.push(this.$route.query.redirect);
          }
        } else {
          alert(res.data.msg || '登录失败');
        }
      } catch (e) {
        alert('登录失败，请检查网络或稍后重试');
      }
    },

    // 短信登录
    async smsLogin() {
      if (!this.smsLoginForm.mobile || !this.smsLoginForm.captcha) {
        alert('请输入手机号和验证码');
        return;
      }
      
      try {
        const res = await smsLogin({
          mobile: this.smsLoginForm.mobile,
          captcha: this.smsLoginForm.captcha
        });
        
        if (res.data && res.data.code === 1) {
          const token = res.data.data.userinfo.token;
          const username = res.data.data.userinfo.username;
          
          localStorage.setItem('userToken', token);
          localStorage.setItem('username', username);
          
          this.isLoggedIn = true;
          this.fetchUserInfo(token);
          this.fetchVipInfo();
          this.smsLoginForm.mobile = '';
          this.smsLoginForm.captcha = '';
          this.loginType = 'password';
          alert('登录成功！');
          window.dispatchEvent(new Event('app:auth-changed'));
          if (this.$route.query.redirect) {
            this.$router.push(this.$route.query.redirect);
          }
        } else {
          alert(res.data.msg || '登录失败');
        }
      } catch (e) {
        alert('登录失败，请稍后重试');
      }
    },

    // 短信注册
    async smsRegister() {
      if (!this.registerForm.mobile || !this.registerForm.captcha || !this.registerForm.password) {
        alert('请填写完整信息');
        return;
      }
      if (!/^1\d{10}$/.test(this.registerForm.mobile)) {
        alert('请输入正确的手机号');
        return;
      }
      if (this.registerForm.password.length < 6) {
        alert('密码至少6位');
        return;
      }
      
      // 调用后端验证短信验证码
      try {
        const checkRes = await checkSmsCode(this.registerForm.mobile, this.registerForm.captcha, 'register');
        if (!checkRes.success) {
          alert('验证码错误或已过期');
          return;
        }
      } catch (e) {
        alert('验证码验证失败');
        return;
      }
      
      try {
        const res = await userRegister({ account: this.registerForm.mobile, password: this.registerForm.password });
        if (res.data && res.data.code === 1) {
          const token = res.data.data.userinfo.token;
          const username = res.data.data.userinfo.username;
          
          localStorage.setItem('userToken', token);
          localStorage.setItem('username', username);
          
          this.isLoggedIn = true;
          this.fetchUserInfo(token);
          this.fetchVipInfo();
          this.registerForm = { mobile: '', captcha: '', password: '' };
          this.loginType = 'password';
          alert('注册成功！');
          window.dispatchEvent(new Event('app:auth-changed'));
          if (this.$route.query.redirect) {
            this.$router.push(this.$route.query.redirect);
          }
        } else {
          alert(res.data.msg || '注册失败');
        }
      } catch (e) {
        alert('注册失败，请检查网络或稍后重试');
      }
    },

    // 发送登录短信验证码
    async sendLoginSms() {
      if (!this.smsLoginForm.mobile) {
        alert('请输入手机号');
        return;
      }
      if (!/^1\d{10}$/.test(this.smsLoginForm.mobile)) {
        alert('请输入正确的手机号');
        return;
      }
      try {
        const res = await sendSmsCode(this.smsLoginForm.mobile, 'mobilelogin');
        if (res.success) {
          this.startSmsCountdown();
          alert('验证码已发送');
        } else {
          alert(res.message || '发送失败');
        }
      } catch (e) {
        console.error('发送短信失败:', e);
        alert('发送失败，请稍后重试: ' + (e.message || e));
      }
    },

    // 短信倒计时
    startSmsCountdown() {
      if (this.smsTimer) clearInterval(this.smsTimer);
      this.smsCountdown = 60;
      this.smsTimer = setInterval(() => {
        this.smsCountdown--;
        if (this.smsCountdown <= 0) {
          clearInterval(this.smsTimer);
          this.smsTimer = null;
        }
      }, 1000);
    },

    // 发送注册短信验证码
    async sendRegisterSms() {
      if (!this.registerForm.mobile) {
        alert('请输入手机号');
        return;
      }
      if (!/^1\d{10}$/.test(this.registerForm.mobile)) {
        alert('请输入正确的手机号');
        return;
      }
      try {
        const res = await sendSmsCode(this.registerForm.mobile, 'register');
        if (res.success) {
          this.startSmsCountdown();
          alert('验证码已发送');
        } else {
          alert(res.message || '发送失败');
        }
      } catch (e) {
       alert('发送失败，请稍后重试: ' + (e.message || e));
      }
    },

    // 提交实名认证
    async submitRealname() {
      if (!this.realnameForm.realname || !this.realnameForm.idcard_front_file) {
        alert('请填写完整信息');
        return;
      }
      this.submittingRealname = true;
      try {
        // 使用 FormData 上传图片
        const formData = new FormData();
        formData.append('realname', this.realnameForm.realname);
        formData.append('idcard_front', this.realnameForm.idcard_front_file);
        if (this.realnameForm.idcard_back_file) {
          formData.append('idcard_back', this.realnameForm.idcard_back_file);
        }
        const res = await realnameVerify(formData);
        if (res.data && res.data.code === 1) {
          alert('实名认证已提交，请等待审核！');
          this.userInfo.realname_verified = 1;
          this.userInfo.realname = res.data.data.realname;
          this.userInfo.idcard_front = res.data.data.idcard_front;
          this.showRealnameModal = false;
          this.realnameForm = { realname: '', idcard_front: '', idcard_back: '', idcard_front_file: null, idcard_back_file: null };
        } else {
          alert(res.data.msg || '认证失败');
        }
      } catch (e) {
        alert('认证失败，请稍后重试');
      } finally {
        this.submittingRealname = false;
      }
    },

    // 触发身份证上传
    triggerUpload(type) {
      if (type === 'front') {
        this.$refs.idcardFrontInput.click();
      } else {
        this.$refs.idcardBackInput.click();
      }
    },

    // 处理身份证正面照上传
    handleIdcardFrontUpload(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('图片大小不能超过5MB');
          return;
        }
        this.realnameForm.idcard_front_file = file;
        // 生成预览
        const reader = new FileReader();
        reader.onload = (e) => {
          this.realnameForm.idcard_front = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    // 处理身份证反面照上传
    handleIdcardBackUpload(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('图片大小不能超过5MB');
          return;
        }
        this.realnameForm.idcard_back_file = file;
        // 生成预览
        const reader = new FileReader();
        reader.onload = (e) => {
          this.realnameForm.idcard_back = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    navigateToYouthMode() {
      this.$router.push('/youth-mode');
    },

    goToAuthorApplication() {
      this.$router.push('/author-application');
    },

    goToReadingHistory() {
      this.$router.push('/reading-history');
    },

    goToFavorites() {
      this.$router.push('/favorites');
    },

    goToMyComments() {
      this.$router.push('/my-comments');
    },

    goToPurchaseHistory() {
      this.$router.push('/purchase-history');
    },

    getAuthorStatusText(status) {
      const map = {
        'none': '未申请',
        'pending': '审核中',
        'approved': '已通过',
        'rejected': '未通过'
      };
      return map[status] || '未申请';
    },

    // 获取用户信息
    async fetchUserInfo(token) {
      try {
        console.log('fetchUserInfo token:', token);
        const res = await getUserInfo(token);
        if (res.data && res.data.code === 1) {
          const info = res.data.data;
          this.userInfo = {
            username: info.username,
            level: 'LV.1',
            registerDate: info.jointime ? new Date(info.jointime * 1000).toLocaleDateString() : '',
            avatar: info.avatar || this.defaultAvatar,
            // 新增字段
            realname_verified: info.realname_verified || 0,
            realname: info.realname || '',
            idcard_last: info.idcard_last || '',
            idcard_front: info.idcard_front || '',
            idcard_back: info.idcard_back || '',
            birthday: info.birthday || '',
            mobile: info.mobile || '',
            is_author: !!info.is_author,
            author_status: info.author_status || 'none'
          };
        }
      } catch (e) { /* ignore */ }
    },

    // 获取用户VIP信息
    async fetchVipInfo() {
      try {
        const token = localStorage.getItem('userToken');
        if (!token) return;
        
        const res = await getUserVipInfo();
        if (res.data && res.data.code === 1) {
          const data = res.data.data;
          this.vipInfo.isVip = data.isVip;
          
          if (data.isVip && data.vipInfo) {
            this.vipInfo.packageName = data.vipInfo.package_name;
            this.vipInfo.endTime = this.formatDate(data.vipInfo.end_time * 1000);
            this.vipInfo.remainingDays = data.vipInfo.remaining_days;
          }
        }
      } catch (e) {
        console.error('获取VIP信息失败', e);
      }
    },
    
    // 将时间戳或日期字符串转换为日期格式
    formatDate(input) {
      if (!input) return '';

      let date;
      if (typeof input === 'string') {
        date = new Date(input);
      } else {
        date = new Date(input);
      }

      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    // 退出登录
    logout() {
      this.isLoggedIn = false;
      localStorage.removeItem('userToken');
      localStorage.removeItem('username');
      window.dispatchEvent(new Event('app:auth-changed'));
      alert('已退出登录');
    },

    // 加载用户数据
    loadUserData() {
      // 使用新的阅读进度管理器加载阅读历史
      this.readingHistory = ReadingProgressManager.getHistory();

      // 获取阅读统计信息
      this.readingStats = ReadingProgressManager.getReadingStats();

      console.log('[Profile] 加载阅读历史:', this.readingHistory.length, '条记录');
      console.log('[Profile] 阅读统计:', this.readingStats);

      // 加载设置
      const settings = localStorage.getItem('userSettings');
      if (settings) {
        this.settings = { ...this.settings, ...JSON.parse(settings) };
      }
    },
    addSampleData() {
      // 示例数据已禁用
      console.log('[Profile] 示例数据功能已禁用');
    },

    // 继续阅读
    continueReading(item) {
      console.log('[Profile] 继续阅读:', item);
      this.$router.push({
        name: 'ComicReader',
        params: {
          id: item.chapterId,
          comicId: item.comicId
        }
      });
    },

    // 删除阅读历史
    removeHistory(comicId) {
      // 直接从 localStorage 删除该漫画的记录
      const history = ReadingProgressManager.getHistory();
      const filteredHistory = history.filter(item => item.comicId !== comicId);
      localStorage.setItem('readingHistory', JSON.stringify(filteredHistory));
      this.readingHistory = filteredHistory;
      this.readingStats = ReadingProgressManager.getReadingStats();
    },

    // 清空所有阅读历史
    clearAllHistory() {
      if (confirm('确定要清空所有阅读历史吗？此操作不可恢复。')) {
        const success = ReadingProgressManager.clearHistory();
        if (success) {
          this.readingHistory = [];
          this.readingStats = ReadingProgressManager.getReadingStats();
          console.log('[Profile] 清空阅读历史成功');
        }
      }
    },

    // 保存设置
    saveSettings() {
      localStorage.setItem('userSettings', JSON.stringify(this.settings));
      alert('设置已保存');
      this.showSettings = false;
    },

    // 投稿管理相关方法

    // 加载投稿列表
    async loadSubmissions() {
      this.loadingSubmissions = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.pageSize
        };

        if (this.submissionFilter) {
          params.audit_status = this.submissionFilter;
        }

        const response = await getUserSubmissions(params);
        if (response.data && response.data.code === 1) {
          const data = response.data.data;
          this.submissions = data.list || [];
          this.totalPages = Math.ceil((data.total || 0) / this.pageSize);
          console.log('投稿列表加载成功:', this.submissions.length, '条');
        } else {
          throw new Error(response.data?.msg || '获取投稿列表失败');
        }
      } catch (error) {
        console.error('加载投稿列表失败:', error);
        alert('加载投稿列表失败: ' + (error.message || '未知错误'));
        this.submissions = [];
      } finally {
        this.loadingSubmissions = false;
      }
    },

    // 显示我的投稿
    async showMySubmissionsModal() {
      this.showMySubmissions = true;
      this.currentPage = 1;
      await this.loadSubmissions();
    },

    // 分页切换
    async changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        await this.loadSubmissions();
      }
    },

    // 获取投稿封面图片
    getSubmissionCover(submission) {
      if (!submission.cover) {
        return 'https://via.placeholder.com/60x80/cccccc/666666?text=封面';
      }

      if (submission.cover.startsWith('http')) {
        return submission.cover;
      }

      return BASE_URL + submission.cover;
    },

    // 获取状态样式类
    getStatusClass(status) {
      const statusMap = {
        'pending': 'status-pending',
        'approved': 'status-approved',
        'rejected': 'status-rejected'
      };
      return statusMap[status] || 'status-unknown';
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        'pending': '待审核',
        'approved': '已通过',
        'rejected': '已拒绝'
      };
      return statusMap[status] || '未知状态';
    },

    // 查看投稿详情
    async viewSubmissionDetail(submission) {
      try {
        const response = await getSubmissionDetail(submission.id);
        if (response.data && response.data.code === 1) {
          const detail = response.data.data;
          alert(`投稿详情:\n标题: ${detail.title}\n状态: ${this.getStatusText(detail.audit_status)}\n创建时间: ${this.formatDate(detail.created_at)}`);
        } else {
          throw new Error(response.data?.msg || '获取详情失败');
        }
      } catch (error) {
        console.error('获取投稿详情失败:', error);
        alert('获取投稿详情失败: ' + (error.message || '未知错误'));
      }
    },

    // 编辑投稿
    editSubmission(submission) {
      this.editingSubmission = {
        id: submission.id,
        title: submission.title,
        description: submission.description,
        tags: submission.tags || ''
      };
      this.showEditSubmission = true;
    },

    // 保存投稿编辑
    async saveSubmissionEdit() {
      if (this.savingEdit) return;

      if (!this.editingSubmission.title.trim()) {
        alert('请输入作品标题');
        return;
      }

      if (!this.editingSubmission.description.trim()) {
        alert('请输入作品描述');
        return;
      }

      this.savingEdit = true;
      try {
        const updateData = {
          title: this.editingSubmission.title,
          description: this.editingSubmission.description,
          tags: this.editingSubmission.tags
        };

        const response = await editSubmission(this.editingSubmission.id, updateData);
        if (response.data && response.data.code === 1) {
          alert('投稿更新成功！');
          this.showEditSubmission = false;
          // 重新加载投稿列表
          await this.loadSubmissions();
        } else {
          throw new Error(response.data?.msg || '更新失败');
        }
      } catch (error) {
        console.error('更新投稿失败:', error);
        alert('更新投稿失败: ' + (error.message || '未知错误'));
      } finally {
        this.savingEdit = false;
      }
    },

    // 删除投稿确认
    deleteSubmissionConfirm(submission) {
      if (confirm(`确定要删除投稿《${submission.title}》吗？此操作不可恢复。`)) {
        this.deleteSubmissionAction(submission.id);
      }
    },

    // 执行删除投稿
    async deleteSubmissionAction(submissionId) {
      try {
        console.log('正在删除投稿ID:', submissionId);
        const response = await deleteSubmission(submissionId);
        console.log('删除投稿API响应:', response);

        if (response.data && response.data.code === 1) {
          alert('投稿删除成功！');
          // 重新加载投稿列表
          await this.loadSubmissions();
        } else {
          const errorMsg = response.data?.msg || response.data?.message || '删除失败，请稍后重试';
          console.error('删除投稿失败，服务器响应:', response.data);
          throw new Error(errorMsg);
        }
      } catch (error) {
        console.error('删除投稿失败:', error);

        // 更详细的错误处理
        let errorMessage = '删除投稿失败';
        if (error.response) {
          // 服务器返回了错误状态码
          errorMessage += `: ${error.response.status} ${error.response.statusText}`;
          if (error.response.data?.msg) {
            errorMessage += ` - ${error.response.data.msg}`;
          }
        } else if (error.message) {
          errorMessage += `: ${error.message}`;
        } else {
          errorMessage += ': 未知错误';
        }

        alert(errorMessage);
      }
    },

    // 格式化时间
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString();
    },

    // 处理封面图片URL
    getCoverImageUrl(coverImage) {
      if (!coverImage) {
        return 'https://via.placeholder.com/60x80/cccccc/666666?text=封面';
      }

      // 如果已经是完整URL，直接返回
      if (coverImage.startsWith('http')) {
        return coverImage;
      }

      // 处理可能存在的 uploads 路径问题
      if (coverImage.includes('/uploads/')) {
        // 如果路径以 /uploads/ 开头，拼接后端地址
        return 'https://web.comicshort.cn' + coverImage;
      }

      // 如果是相对路径，拼接BASE_URL
      return BASE_URL + coverImage;
    },

    // 修改资料相关方法

    // 打开修改资料弹窗
    openEditProfile() {
      this.initProfileForm();
      this.showEditProfile = true;
    },

    // 初始化资料表单
    initProfileForm() {
      this.profileForm = {
        avatar: this.userInfo.avatar || '',
        username: this.userInfo.username || '',
        nickname: this.userInfo.nickname || this.userInfo.username || '',
        bio: this.userInfo.bio || ''
      };
    },

    // 处理头像上传
    async handleAvatarUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件');
        return;
      }

      // 验证文件大小（2MB）
      if (file.size > 2 * 1024 * 1024) {
        alert('图片大小不能超过 2MB');
        return;
      }

      this.uploadingAvatar = true;
      try {
        const response = await uploadFile(file, { category: 'avatar' });
        if (response.data && response.data.code === 1) {
          let avatarUrl = response.data.data.url;
          // 如果是相对路径，拼接BASE_URL
          if (avatarUrl && !avatarUrl.startsWith('http')) {
            avatarUrl = BASE_URL + avatarUrl;
          }
          this.profileForm.avatar = avatarUrl;
          alert('头像上传成功！');
        } else {
          throw new Error(response.data?.msg || '上传失败');
        }
      } catch (error) {
        console.error('头像上传失败:', error);
        alert('头像上传失败: ' + (error.message || '未知错误'));
      } finally {
        this.uploadingAvatar = false;
        // 清空文件输入框
        event.target.value = '';
      }
    },

    // 保存资料修改
    async saveProfile() {
      // 表单验证
      if (!this.profileForm.username.trim()) {
        alert('请输入用户名');
        return;
      }

      if (!this.profileForm.nickname.trim()) {
        alert('请输入昵称');
        return;
      }

      if (this.profileForm.username.length < 2 || this.profileForm.username.length > 20) {
        alert('用户名长度应在2-20个字符之间');
        return;
      }

      if (this.profileForm.nickname.length < 2 || this.profileForm.nickname.length > 20) {
        alert('昵称长度应在2-20个字符之间');
        return;
      }

      this.savingProfile = true;
      try {
        const profileData = {
          avatar: this.profileForm.avatar,
          username: this.profileForm.username.trim(),
          nickname: this.profileForm.nickname.trim(),
          bio: this.profileForm.bio.trim()
        };

        const response = await updateUserProfile(profileData);
        
        if (response.data && response.data.code === 1) {
          // 更新本地用户信息
          this.userInfo = {
            ...this.userInfo,
            ...profileData
          };
          
          // 更新localStorage中的用户名（如果有变化）
          if (profileData.username !== localStorage.getItem('username')) {
            localStorage.setItem('username', profileData.username);
          }

          alert('资料修改成功！');
          this.showEditProfile = false;
        } else {
          throw new Error(response.data?.msg || '修改失败');
        }
      } catch (error) {
        console.error('资料修改失败:', error);
        let errorMessage = '资料修改失败';
        
        if (error.response?.data?.msg) {
          errorMessage = error.response.data.msg;
        } else if (error.message) {
          errorMessage = error.message;
        }

        // 处理特定错误提示
        if (errorMessage.includes('用户名已存在') || errorMessage.includes('username')) {
          errorMessage = '用户名已被使用，请选择其他用户名';
        } else if (errorMessage.includes('昵称已存在') || errorMessage.includes('nickname')) {
          errorMessage = '昵称已被使用，请选择其他昵称';
        }

        alert(errorMessage);
      } finally {
        this.savingProfile = false;
      }
    }
  }
}
</script>

<style scoped>
.profile-page {
  width: 50vw;
  min-width: 800px;
  max-width: none;
  min-height: 100vh;
  margin: 0 auto;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
  position: relative;
  padding: 30px;
}

/* 登录区域 */
.login-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: 40px;
}

.login-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 50px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 500px;
}

.login-card h2 {
  text-align: center;
  margin-bottom: 25px;
  color: var(--text);
  font-size: 24px;
  font-weight: 600;
}

/* 登录方式切换 */
.login-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 25px;
  border-bottom: 2px solid var(--border);
}

.login-tabs button {
  flex: 1;
  padding: 12px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-dim);
  transition: all 0.3s;
}

.login-tabs button.active {
  color: var(--brand);
  border-bottom-color: var(--brand);
  font-weight: 600;
}

.login-tabs button:hover:not(.active) {
  color: var(--text);
}

/* 短信验证码输入组 */
.sms-code-group {
  display: flex;
  gap: 10px;
}

.sms-code-group .form-input {
  flex: 1;
}

.sms-code-btn {
  padding: 12px 16px;
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
  transition: all 0.3s;
  min-width: 100px;
}

.sms-code-btn:hover:not(:disabled) {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

.sms-code-btn:disabled {
  background: var(--surface-3);
  color: var(--text-faint);
  cursor: not-allowed;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: var(--text);
  font-weight: 500;
}

.form-input {
  width: 100%;
  padding: 12px;
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 16px;
  box-sizing: border-box;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.form-input:-ms-input-placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.form-input:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 2px var(--brand-soft);
}

.login-btn, .register-btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 10px;
  transition: all 0.2s;
}

.login-btn {
  background: var(--brand);
  color: white;
}

.login-btn:hover {
  background: var(--brand-hover);
}

.register-btn {
  background: var(--surface-3);
  color: var(--text);
  border: 1px solid var(--border);
}

.register-btn:hover {
  background: var(--brand-soft);
  border-color: var(--brand);
}

/* 个人中心内容 */
.profile-content {
  padding: 20px;
}

/* 用户信息卡片 */
.user-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 25px;
}

.user-avatar img {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
}

.user-info h3 {
  margin: 0 0 8px 0;
  color: var(--text);
  font-size: 22px;
  font-weight: 600;
}

.user-info p {
  margin: 4px 0;
  color: var(--text-dim);
  font-size: 15px;
}

/* VIP信息样式 */
.vip-info {
  margin-top: 15px;
  padding: 10px;
  background: var(--brand-soft);
  border: 1px solid var(--brand);
  border-radius: 8px;
}

.vip-badge {
  display: inline-block;
  background: var(--brand);
  color: white;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 4px;
  margin-right: 5px;
}

.vip-details {
  color: var(--text) !important;
  font-weight: bold;
  margin-top: 8px !important;
}

.vip-expiry, .vip-days {
  color: var(--text-dim) !important;
  font-size: 12px !important;
  margin: 4px 0 !important;
}

.non-vip {
  margin-top: 10px;
  padding: 8px;
  background-color: var(--surface-3);
  border-radius: 8px;
  text-align: center;
}

.logout-btn {
  margin-left: auto;
  padding: 8px 16px;
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.logout-btn:hover {
  opacity: 0.85;
}

/* 功能列表 */
.feature-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  cursor: pointer;
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.feature-item:hover {
  transform: translateY(-2px);
  border-color: var(--brand);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.feature-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--brand-soft);
  border: 1px solid var(--brand);
  color: var(--brand);
}

.feature-icon svg {
  width: 21px;
  height: 21px;
}

.feature-text {
  flex: 1;
  min-width: 0;
}

.feature-title {
  color: var(--text);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
}

.feature-sub {
  margin-top: 3px;
  color: var(--text-dim);
  font-size: 13px;
  line-height: 1.35;
}

.feature-arrow {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  color: var(--text-dim);
  transition: transform 0.25s ease, color 0.25s ease;
}

.feature-item:hover .feature-arrow {
  color: var(--brand);
  transform: translateX(3px);
}
.panel-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
}
.panel-action-btn {
  background: var(--brand);
  color: #fff;
  border: none;
  padding: 10px 24px;
  border-radius: 20px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
}
.panel-action-btn:hover {
  background: var(--brand-hover);
  transform: translateY(-1px);
}
@media (max-width: 640px) {
  .tab-bar-item {
    flex: 1 0 30%;
    font-size: 13px;
    padding: 8px 10px;
  }
}

/* Tab 面板列表 */
.panel-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}
.panel-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s;
}
.panel-list-item:hover {
  border-color: var(--brand-soft);
}
.panel-thumb {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}
.panel-text {
  min-width: 0;
  flex: 1;
}
.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.panel-sub {
  font-size: 12px;
  color: var(--text-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
}
.panel-empty {
  color: var(--text-faint);
  font-size: 14px;
  padding: 24px 0;
  text-align: center;
}
.panel-info-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}
.panel-info-status {
  font-size: 16px;
  font-weight: 700;
  color: var(--brand);
}
.panel-info-desc {
  color: var(--text-dim);
  font-size: 13px;
  line-height: 1.5;
  max-width: 360px;
  margin: 0;
}
.panel-action-btn {
  margin-top: 4px;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(12, 25, 41, 0.94);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: linear-gradient(180deg, #1a2f4a 0%, #0f1c2e 100%);
  border: 1px solid rgba(96, 165, 250, 0.25);
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.55);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border);
}

.modal-header h3 {
  margin: 0;
  color: var(--text);
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-dim);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: var(--text);
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

/* 阅读历史弹窗特殊样式 */
.reading-history-modal {
  max-width: 1000px;
  max-height: 90vh;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.clear-all-btn {
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
}

.clear-all-btn:hover {
  opacity: 0.85;
}

/* 阅读统计 */
.reading-stats {
  display: flex;
  justify-content: space-around;
  padding: 20px;
  background: var(--surface-2);
  border-bottom: 1px solid var(--border);
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-number {
  display: block;
  font-size: 24px;
  font-weight: bold;
  color: var(--brand);
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: var(--text-dim);
  text-transform: uppercase;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-dim);
}

.empty-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  color: var(--text-faint);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon svg {
  width: 100%;
  height: 100%;
}

.empty-tip {
  font-size: 12px;
  color: var(--text-faint);
  margin-top: 8px;
}

/* 阅读历史列表 */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.history-item {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  background: var(--surface-2);
}

.history-item:hover {
  background: var(--surface-3);
  border-color: var(--brand);
  transform: translateY(-2px);
}

.history-cover {
  width: 60px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 16px;
}

.history-info {
  flex: 1;
  min-width: 0;
}

.history-info h4 {
  margin: 0 0 6px 0;
  color: var(--text);
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chapter-title {
  margin: 0 0 8px 0;
  color: var(--text-dim);
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.progress-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.progress-text {
  font-size: 12px;
  color: var(--text-dim);
  min-width: 60px;
}

.progress-bar {
  flex: 1;
  height: 4px;
  background: var(--surface-3);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--brand);
  transition: width 0.3s ease;
}

.progress-percent {
  font-size: 12px;
  color: var(--brand);
  font-weight: 500;
  min-width: 35px;
  text-align: right;
}

.history-time {
  color: var(--text-faint);
  font-size: 12px;
  margin: 0;
}

.history-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.completed-badge {
  background: var(--success);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
}

.remove-btn {
  padding: 4px 8px;
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
}

.remove-btn:hover {
  opacity: 0.85;
  transform: scale(1.05);
}

/* 设置项 */
.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid var(--border);
}

.setting-item:last-child {
  border-bottom: none;
}

.setting-item label {
  color: var(--text);
  font-weight: 500;
}

.setting-item select {
  padding: 8px;
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 4px;
}

.save-btn {
  width: 100%;
  padding: 12px;
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 20px;
}

.save-btn:hover {
  background: var(--brand-hover);
}

/* 投稿管理样式 */
.submissions-modal {
  max-width: 1200px;
  max-height: 90vh;
}

.filter-select {
  padding: 6px 12px;
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 14px;
}

.loading-state {
  text-align: center;
  padding: 40px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--surface-3);
  border-top: 4px solid var(--brand);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.submissions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.submission-item {
  display: flex;
  align-items: flex-start;
  padding: 20px;
  background: var(--surface-2);
  border-radius: 12px;
  border: 1px solid var(--border);
  transition: all 0.3s ease;
}

.submission-item:hover {
  background: var(--brand-soft);
  border-color: var(--brand);
  transform: translateY(-2px);
}

.submission-cover {
  width: 80px;
  height: 100px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 20px;
}

.submission-info {
  flex: 1;
  min-width: 0;
}

.submission-info h4 {
  margin: 0 0 8px 0;
  color: var(--text);
  font-size: 18px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.submission-author {
  color: var(--text-dim);
  font-size: 14px;
  margin: 0 0 8px 0;
}

.submission-desc {
  color: var(--text-dim);
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.submission-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.submission-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-pending {
  background: rgba(251, 191, 36, 0.12);
  color: var(--warning);
  border: 1px solid rgba(251, 191, 36, 0.35);
}

.status-approved {
  background: rgba(52, 211, 153, 0.12);
  color: var(--success);
  border: 1px solid rgba(52, 211, 153, 0.35);
}

.status-rejected {
  background: rgba(248, 113, 113, 0.12);
  color: var(--danger);
  border: 1px solid rgba(248, 113, 113, 0.35);
}

.status-unknown {
  background: var(--surface-3);
  color: var(--text-dim);
  border: 1px solid var(--border);
}

.submission-date,
.submission-chapters {
  font-size: 12px;
  color: var(--text-dim);
}

.submission-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 16px;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 60px;
}

.view-btn {
  background: var(--brand);
  color: white;
}

.view-btn:hover {
  background: var(--brand-hover);
}

.edit-btn {
  background: var(--brand-soft);
  color: var(--brand);
  border: 1px solid var(--brand);
}

.edit-btn:hover {
  background: var(--brand-soft-strong);
}

.delete-btn {
  background: var(--danger);
  color: white;
}

.delete-btn:hover {
  opacity: 0.85;
}

/* 分页样式 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 20px 0;
}

.page-btn {
  padding: 8px 16px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text);
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: var(--brand);
  color: white;
  border-color: var(--brand);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: var(--text-dim);
  font-weight: 500;
}

/* 编辑投稿模态框样式 */
.edit-submission-modal {
  max-width: 600px;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.edit-form .form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edit-form label {
  font-weight: 500;
  color: var(--text);
  font-size: 14px;
}

.edit-form input,
.edit-form textarea {
  padding: 12px;
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.edit-form input:focus,
.edit-form textarea:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-soft);
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
}

.cancel-btn {
  padding: 10px 20px;
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-dim);
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: var(--brand-soft);
  border-color: var(--brand);
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .profile-page {
    width: 70vw;
    min-width: 600px;
  }
}

@media (max-width: 768px) {
  .profile-page {
    width: 95vw;
    min-width: auto;
    padding: 20px;
    margin: 10px auto;
  }

  .login-card {
    padding: 30px 20px;
    max-width: 100%;
  }

  .user-card {
    flex-direction: column;
    text-align: center;
    padding: 20px;
  }

  .logout-btn {
    margin: 10px 0 0 0;
  }

  .menu-item {
    padding: 18px;
  }

  .modal-content {
    max-width: 95vw;
    margin: 20px;
  }

  .reading-history-modal,
  .submissions-modal,
  .edit-submission-modal {
    max-width: 95vw;
  }

  .submission-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .submission-cover {
    width: 60px;
    height: 80px;
    margin-right: 0;
    align-self: center;
  }

  .submission-actions {
    flex-direction: row;
    align-self: stretch;
    margin-left: 0;
  }

  .action-btn {
    flex: 1;
  }

  .filter-select {
    font-size: 12px;
    padding: 4px 8px;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
  }

  .page-btn {
    padding: 10px 20px;
  }
}

/* 修改资料弹窗样式 */
.edit-profile-modal {
  max-width: 500px;
  width: 90%;
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: var(--text);
  font-size: 14px;
}

.form-input {
  padding: 12px 16px;
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.form-input:-ms-input-placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.form-input:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-soft);
}

.form-textarea {
  padding: 12px 16px;
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all 0.3s ease;
}

.form-textarea:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-soft);
}

.char-count {
  font-size: 12px;
  color: var(--text-dim);
  text-align: right;
  margin-top: 4px;
}

/* 头像上传区域 */
.avatar-group {
  align-items: center;
}

.avatar-upload {
  display: flex;
  align-items: center;
  gap: 20px;
}

.current-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid var(--border-strong);
  flex-shrink: 0;
}

.current-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.upload-btn {
  padding: 10px 20px;
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
}

.upload-btn:hover:not(:disabled) {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

.upload-btn:disabled {
  background: var(--surface-3);
  color: var(--text-faint);
  cursor: not-allowed;
  transform: none;
}

/* 弹窗操作按钮 */
.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border);
}

.cancel-btn {
  padding: 12px 24px;
  background: var(--surface-3);
  color: var(--text-dim);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: var(--brand-soft);
  border-color: var(--brand);
  color: var(--text);
}

.save-btn {
  padding: 12px 24px;
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;
}

.save-btn:hover:not(:disabled) {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

.save-btn:disabled {
  background: var(--surface-3);
  color: var(--text-faint);
  cursor: not-allowed;
  transform: none;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .edit-profile-modal {
    max-width: 95%;
    margin: 20px auto;
  }

  .avatar-upload {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }

  .modal-actions {
    flex-direction: column;
  }

  .cancel-btn,
  .save-btn {
    width: 100%;
  }
}

/* 实名认证弹窗样式 */
.realname-modal {
  max-width: 500px;
}

.verified-info {
  text-align: center;
  padding: 30px 0;
}

.verified-icon {
  width: 60px;
  height: 60px;
  background: var(--success);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.verified-icon svg {
  width: 30px;
  height: 30px;
}

.verified-text {
  font-size: 18px;
  font-weight: 600;
  color: var(--success);
  margin-bottom: 16px;
}

.verified-detail {
  font-size: 14px;
  color: var(--text-dim);
  margin: 8px 0;
}

.realname-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.idcard-upload-area {
  border: 2px dashed var(--border-strong);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: var(--surface);
}

.idcard-upload-area:hover {
  border-color: var(--brand);
  background: var(--brand-soft);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: var(--text-faint);
}

.upload-icon {
  font-size: 36px;
  color: var(--text-faint);
}

.upload-preview {
  position: relative;
}

.upload-preview img {
  max-width: 100%;
  max-height: 150px;
  border-radius: 4px;
}

.preview-tip {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: var(--text-dim);
}

.upload-tip {
  font-size: 12px;
  color: var(--text-faint);
  margin-top: 8px;
  text-align: left;
}

.form-tip {
  font-size: 12px;
  color: var(--text-faint);
  text-align: center;
}

/* ===== 内联 Tab 页面样式（带 tab- 前缀，避免与已有类冲突） ===== */
.tab-loading-state,
.tab-empty-state {
  text-align: center;
  padding: 50px 20px;
  color: var(--text-dim);
}
.tab-loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--surface-3);
  border-top-color: var(--brand);
  border-radius: 50%;
  animation: tab-spin 1s linear infinite;
  margin: 0 auto 16px;
}
@keyframes tab-spin { to { transform: rotate(360deg); } }
.tab-empty-icon {
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
  opacity: 0.5;
}
.tab-btn-primary {
  background: var(--brand);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}
.tab-btn-primary:hover:not(:disabled) {
  background: var(--brand-hover);
  transform: translateY(-1px);
}
.tab-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.tab-btn-secondary {
  background: var(--surface-3);
  color: var(--text);
  border: 1px solid var(--border);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}
.tab-btn-secondary:hover {
  background: var(--brand-soft);
  border-color: var(--brand);
}

/* 阅读进度（ReadingHistory） */
.tab-rh-wrap { align-items: stretch; }
.tab-rh-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
}
.tab-rh-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  transition: all 0.3s ease;
}
.tab-rh-card:hover {
  border-color: var(--brand);
  transform: translateY(-1px);
}
.tab-rh-cover {
  width: 80px;
  height: 106px;
  object-fit: cover;
  border-radius: 5px;
  flex-shrink: 0;
}
.tab-rh-info { flex: 1; min-width: 0; }
.tab-rh-title {
  margin: 0 0 6px 0;
  font-size: 16px;
  color: var(--text);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tab-rh-chapter,
.tab-rh-page,
.tab-rh-time {
  margin: 0 0 3px 0;
  font-size: 12px;
  color: var(--text-dim);
}
.tab-rh-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* 我的收藏（Favorites） */
.tab-fav-wrap { align-items: stretch; }
.tab-fav-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 18px;
  width: 100%;
}
.tab-fav-card {
  position: relative;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}
.tab-fav-card:hover {
  border-color: var(--brand);
  transform: translateY(-3px);
}
.tab-fav-cover {
  width: 100%;
  aspect-ratio: 3/4;
  object-fit: cover;
  display: block;
}
.tab-fav-info { padding: 10px; }
.tab-fav-title {
  margin: 0 0 5px 0;
  font-size: 14px;
  color: var(--text);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tab-fav-author {
  margin: 0 0 6px 0;
  font-size: 12px;
  color: var(--text-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tab-fav-status {
  display: inline-block;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 99px;
  background: var(--brand-soft);
  color: var(--brand);
}
.tab-fav-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}
.tab-fav-card:hover .tab-fav-remove { opacity: 1; }
.tab-fav-remove:hover { background: var(--danger); }

/* 我的评论（MyComments） */
.tab-cmt-wrap { align-items: stretch; }
.tab-cmt-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
}
.tab-cmt-card {
  display: flex;
  gap: 14px;
  padding: 14px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 8px;
}
.tab-cmt-comic {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 90px;
  flex-shrink: 0;
  cursor: pointer;
}
.tab-cmt-cover {
  width: 80px;
  height: 106px;
  object-fit: cover;
  border-radius: 5px;
}
.tab-cmt-comic-title {
  font-size: 12px;
  color: var(--text);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}
.tab-cmt-body { flex: 1; min-width: 0; }
.tab-cmt-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.tab-cmt-time { font-size: 12px; color: var(--text-dim); }
.tab-cmt-delete {
  background: transparent;
  color: var(--danger);
  border: 1px solid var(--danger);
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.tab-cmt-delete:hover {
  background: var(--danger);
  color: #fff;
}
.tab-cmt-rating {
  display: flex;
  gap: 2px;
  margin-bottom: 8px;
}
.tab-cmt-star {
  width: 14px;
  height: 14px;
  color: var(--text-dim);
}
.tab-cmt-star.filled { color: #fbbf24; }
.tab-cmt-content {
  margin: 0;
  font-size: 14px;
  color: var(--text);
  line-height: 1.6;
  word-break: break-word;
}

/* 购买历史（PurchaseHistory 风格，章节购买） */
.tab-pur-wrap { align-items: stretch; }
.tab-pur-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.tab-pur-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  transition: border-color 0.2s;
}
.tab-pur-card:hover { border-color: var(--border-strong, var(--brand)); }
.tab-pur-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}
.tab-pur-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--surface-3);
  color: var(--text-dim);
}
.tab-pur-icon svg { width: 22px; height: 22px; }
.tab-pur-icon.chapter {
  background: rgba(79, 172, 254, 0.16);
  color: #7dd3fc;
}
.tab-pur-info { min-width: 0; }
.tab-pur-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tab-pur-meta {
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: var(--text-faint);
}
.tab-pur-type {
  background: var(--brand-soft);
  color: #bfe3ff;
  padding: 1px 8px;
  border-radius: 99px;
}
.tab-pur-side {
  text-align: right;
  flex-shrink: 0;
}
.tab-pur-amount {
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 4px;
}
.tab-pur-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 99px;
  display: inline-block;
}
.tab-pur-status.success {
  background: rgba(52, 211, 153, 0.16);
  color: #34d399;
}
.tab-pur-loadmore {
  text-align: center;
  margin-top: 20px;
  width: 100%;
}

@media (max-width: 768px) {
  .tab-rh-card { flex-direction: column; align-items: flex-start; }
  .tab-rh-actions { width: 100%; justify-content: flex-end; }
  .tab-fav-grid { grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); }
  .tab-cmt-card { flex-direction: column; }
  .tab-cmt-comic { flex-direction: row; width: 100%; }
  .tab-cmt-cover { width: 50px; height: 66px; }
  .tab-pur-card { flex-direction: column; align-items: flex-start; }
  .tab-pur-side {
    text-align: left;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

</style> 